<template>
  <div
    :class="['email-component', 'project-ticket-component', { selected }]"
    :style="computedStyles"
    @click="handleClick"
  >
    <div class="ticket-container">
      <!-- Ticket Header -->
      <div class="ticket-header" :style="headerStyles">
        <!-- Ticket Number (always shown) -->
        <div class="ticket-number" :style="ticketNumberStyles">
          <i class="tim-icons icon-bullet-list-67" :style="iconStyles"></i>
          <span>PROJ-123</span>
        </div>

        <!-- Assignee (if enabled) -->
        <div
          v-if="componentData.showAssignee"
          class="ticket-assignee"
          :style="assigneeStyles"
        >
          <span :style="iconStyles">👤</span>
          <span>John Doe</span>
        </div>
      </div>

      <!-- Project Info (if enabled) -->
      <div
        v-if="componentData.showProjectName || componentData.showProjectKey"
        class="project-info"
        :style="projectInfoStyles"
      >
        <span v-if="componentData.showProjectKey" class="project-key" :style="projectKeyStyles">
          [PROJ]
        </span>
        <span v-if="componentData.showProjectName" class="project-name" :style="projectNameStyles">
          Sample Project
        </span>
      </div>

      <!-- Ticket Title (Summary) - if enabled -->
      <h3
        v-if="componentData.showTitle"
        class="ticket-title"
        :style="titleStyles"
      >
        Implement new feature for customer dashboard
      </h3>

      <!-- Ticket Description - if enabled -->
      <div
        v-if="componentData.showDescription"
        class="ticket-description"
        :style="descriptionStyles"
      >
        This ticket involves creating a new analytics section in the customer dashboard that displays key metrics and visualizations.
      </div>

      <!-- Time Estimate - if enabled -->
      <div
        v-if="componentData.showTimeEstimate"
        class="ticket-meta"
        :style="metaStyles"
      >
        <span :style="iconStyles">⏰</span>
        <span>{{ getLabel('timeEstimate') }}: 4h 30m</span>
      </div>

      <!-- Latest Comment - if enabled -->
      <div
        v-if="componentData.showLatestComment"
        class="latest-comment"
        :style="commentStyles"
      >
        <div class="comment-header" :style="commentHeaderStyles">
          <span :style="iconStyles">💬</span>
          <span>{{ getLabel('latestComment') }}</span>
        </div>
        <div class="comment-text" :style="commentTextStyles">
          "Updated the design mockups based on feedback. Ready for review."
        </div>
      </div>

      <!-- Button - if enabled -->
      <div
        v-if="componentData.showButton"
        class="ticket-button-container"
        :style="buttonContainerStyles"
      >
        <a href="#" class="ticket-button" :style="buttonStyles">
          {{ componentData.buttonText || 'View Ticket' }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProjectTicketComponent',
  props: ['componentData', 'selected'],
  data() {
    return {
      labels: {
        english: {
          timeEstimate: 'Time Estimate',
          latestComment: 'Latest Comment',
          assignedTo: 'Assigned to'
        },
        spanish: {
          timeEstimate: 'Tiempo Estimado',
          latestComment: 'Último Comentario',
          assignedTo: 'Asignado a'
        },
        french: {
          timeEstimate: 'Temps Estimé',
          latestComment: 'Dernier Commentaire',
          assignedTo: 'Assigné à'
        },
        swedish: {
          timeEstimate: 'Tidsuppskattning',
          latestComment: 'Senaste Kommentar',
          assignedTo: 'Tilldelad till'
        },
        danish: {
          timeEstimate: 'Tidsestimat',
          latestComment: 'Seneste kommentar',
          assignedTo: 'Tildelt til'
        },
        greek: {
          timeEstimate: 'Εκτιμώμενος Χρόνος',
          latestComment: 'Τελευταίο Σχόλιο',
          assignedTo: 'Ανατέθηκε σε'
        }
      }
    }
  },
  computed: {
    currentLanguage() {
      return this.componentData.language || 'english'
    },

    computedStyles() {
      const styles = this.componentData.styles || {}
      const backgroundColor = styles.backgroundColor === 'inherit' || !styles.backgroundColor ?
        'transparent' : styles.backgroundColor

      return {
        backgroundColor: backgroundColor,
        padding: `${styles.paddingTop || '20'}px ${styles.paddingLeft || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingRight || '20'}px`,
        marginTop: `${styles.marginTop || '16'}px`,
        marginBottom: `${styles.marginBottom || '16'}px`,
        borderRadius: `${styles.borderRadius || '8'}px`,
        border: styles.showBorder ? `1px solid ${styles.borderColor || '#e5e7eb'}` : 'none',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    headerStyles() {
      const styles = this.componentData.styles || {}
      return {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
        paddingBottom: '12px',
        borderBottom: `1px solid ${styles.dividerColor || '#e5e7eb'}`
      }
    },

    ticketNumberStyles() {
      const styles = this.componentData.styles || {}
      return {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: `${styles.ticketNumberFontSize || '14'}px`,
        fontWeight: styles.ticketNumberFontWeight || '600',
        color: styles.ticketNumberColor || '#6b7280',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    assigneeStyles() {
      const styles = this.componentData.styles || {}
      return {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: `${styles.assigneeFontSize || '13'}px`,
        color: styles.assigneeColor || '#6b7280',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    projectInfoStyles() {
      const styles = this.componentData.styles || {}
      return {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px'
      }
    },

    projectKeyStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.projectKeyFontSize || '12'}px`,
        fontWeight: styles.projectKeyFontWeight || '600',
        color: styles.projectKeyColor || '#e14eca',
        backgroundColor: styles.projectKeyBackgroundColor || 'rgba(225, 78, 202, 0.1)',
        padding: '2px 8px',
        borderRadius: '4px',
        fontFamily: 'Monaco, Courier, monospace'
      }
    },

    projectNameStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.projectNameFontSize || '13'}px`,
        fontWeight: styles.projectNameFontWeight || '500',
        color: styles.projectNameColor || '#4b5563',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    titleStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.titleFontSize || '20'}px`,
        fontWeight: styles.titleFontWeight || '700',
        color: styles.titleColor || '#111827',
        margin: '0 0 12px 0',
        lineHeight: '1.3',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    descriptionStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.descriptionFontSize || '14'}px`,
        color: styles.descriptionColor || '#4b5563',
        lineHeight: '1.6',
        marginBottom: '12px',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    metaStyles() {
      const styles = this.componentData.styles || {}
      return {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: `${styles.metaFontSize || '13'}px`,
        color: styles.metaColor || '#6b7280',
        marginBottom: '12px',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    commentStyles() {
      const styles = this.componentData.styles || {}
      return {
        backgroundColor: styles.commentBackgroundColor || '#f9fafb',
        border: `1px solid ${styles.commentBorderColor || '#e5e7eb'}`,
        borderRadius: '6px',
        padding: '12px',
        marginBottom: '16px'
      }
    },

    commentHeaderStyles() {
      const styles = this.componentData.styles || {}
      return {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: `${styles.commentHeaderFontSize || '12'}px`,
        fontWeight: '600',
        color: styles.commentHeaderColor || '#6b7280',
        marginBottom: '6px',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    commentTextStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: `${styles.commentTextFontSize || '13'}px`,
        color: styles.commentTextColor || '#4b5563',
        lineHeight: '1.5',
        fontStyle: 'italic',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    buttonContainerStyles() {
      return {
        textAlign: this.componentData.buttonAlignment || 'left',
        marginTop: '16px'
      }
    },

    buttonStyles() {
      const styles = this.componentData.styles || {}
      const size = this.componentData.buttonSize || 'medium'

      const sizeMap = {
        small: { padding: '8px 16px', fontSize: '14px' },
        medium: { padding: '12px 24px', fontSize: '16px' },
        large: { padding: '16px 32px', fontSize: '18px' }
      }

      const sizeStyles = sizeMap[size] || sizeMap.medium

      return {
        display: 'inline-block',
        padding: sizeStyles.padding,
        fontSize: sizeStyles.fontSize,
        fontWeight: '500',
        color: styles.buttonTextColor || '#ffffff',
        backgroundColor: styles.buttonBackgroundColor || '#e14eca',
        border: 'none',
        borderRadius: `${styles.buttonBorderRadius || '6'}px`,
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s',
        fontFamily: styles.fontFamily || 'Arial, sans-serif'
      }
    },

    iconStyles() {
      const styles = this.componentData.styles || {}
      return {
        fontSize: '14px',
        color: styles.iconColor || '#9ca3af'
      }
    }
  },
  methods: {
    handleClick() {
      this.$emit('click')
    },

    getLabel(key) {
      const languageLabels = this.labels[this.currentLanguage] || this.labels.english
      return languageLabels[key] || key
    }
  }
}
</script>

<style scoped>
.project-ticket-component {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  width: 100%;
  box-sizing: border-box;
}

.project-ticket-component:hover,
.project-ticket-component.selected {
  border-color: #e14eca !important;
  box-shadow: 0 0 0 4px rgba(225, 78, 202, 0.1);
}

.ticket-container {
  width: 100%;
}

.ticket-header,
.project-info,
.ticket-meta,
.comment-header {
  flex-wrap: wrap;
}

.ticket-title,
.ticket-description,
.comment-text {
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.ticket-button {
  transition: all 0.2s;
}

.ticket-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .ticket-header {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 8px;
  }

  .project-info {
    flex-direction: column;
    align-items: flex-start !important;
  }
}
</style>
